import cn from 'classnames'
import { memo, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { ReactComponent as SpecPrice } from 'clickNgo/assets/icons/spec-price.svg'
import amplifyPartnerProgramLogo from 'clickNgo/assets/images/hp_amplify_black_logo.png'
import { AppId } from 'clickNgo/configs/app'
import {
  FrequentIcon,
  HPOrgRankIcon,
  useEngagementPrediction,
} from 'clickNgo/entities/engagementPrediction'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Tooltip } from 'core-components/library/tooltip'
import { selectAppId } from 'core-components/sitebuilder/store/selectors/selectAppId'

import { CONTRACT_STATUS_VISIBLE_CODE } from './configs'
import { companyDetailsFlagsTranslations } from './translation'

import styles from './CompanyDetailsFlags.module.scss'

type Props = {
  isAmplifyMember: boolean
  specialPricingFlag: string
  agreements: string[]
  partnerId: string
}

export const CompanyDetailsFlags = memo<Props>(
  ({ agreements, isAmplifyMember, specialPricingFlag, partnerId }) => {
    const appId = useSelector(selectAppId)
    const isHpscoutso = appId === AppId.HPScoutSO

    const engagementPrediction = useEngagementPrediction(partnerId, {
      showNotificationOnError: true,
    })
    const hpOrgRank = engagementPrediction.data?.hpOrgRank ?? null
    const freqFlag = engagementPrediction.data?.flagAll ?? null

    const hasSpecialPricingFlag = useMemo(() => specialPricingFlag === 'Y', [specialPricingFlag])

    const isContractStatusVisible = useMemo(() => {
      if (isHpscoutso) {
        return Boolean(agreements.length)
      }
      return agreements.includes(CONTRACT_STATUS_VISIBLE_CODE)
    }, [agreements, isHpscoutso])

    const contractStatusTooltip = useCallback(
      () => <Translation text={companyDetailsFlagsTranslations.flags.contractStatus} />,
      []
    )

    const orgStatusTooltip = useCallback(
      () => <Translation text={companyDetailsFlagsTranslations.flags.orgStatus} />,
      []
    )

    const amplifyProgramTooltip = useCallback(
      () => <Translation text={companyDetailsFlagsTranslations.flags.amplifyProgram} />,
      []
    )

    return (
      <div className={styles.container}>
        <RenderWhen condition={Boolean(hpOrgRank)}>
          <HPOrgRankIcon hpOrgRank={hpOrgRank} />
        </RenderWhen>
        <FrequentIcon frequentFlag={freqFlag} className={styles.icon} />
        <RenderWhen condition={isContractStatusVisible}>
          <Tooltip tipContent={contractStatusTooltip}>
            <i className={cn(styles.icon, 'hpicon hpicon-status-contract')} />
          </Tooltip>
        </RenderWhen>
        <RenderWhen condition={hasSpecialPricingFlag}>
          <Tooltip tipContent={orgStatusTooltip}>
            <div className={styles.specPrice}>
              <SpecPrice />
            </div>
          </Tooltip>
        </RenderWhen>
        <RenderWhen condition={isAmplifyMember}>
          <Tooltip tipContent={amplifyProgramTooltip}>
            <div className={styles.amplifyProgram}>
              <img src={amplifyPartnerProgramLogo} alt="Amplify Program" title="Amplify Program" />
            </div>
          </Tooltip>
        </RenderWhen>
      </div>
    )
  }
)

CompanyDetailsFlags.displayName = 'CompanyDetailsFlags'
